import { useState, useCallback } from 'react';
import { FeedbackContextService, type FeedbackContextInfo } from '@/services/feedbackContext';
import { useAuth } from '@/components/auth';
import { toast } from 'sonner';

export interface FeedbackSubmission {
  feedbackText: string;
  submissionContext: string;
  sessionId?: string;
  participantId?: string;
}

/**
 * Hook for managing feedback submission with context awareness
 * Implements requirements 2.4, 2.5, 6.2, 6.3
 */
export function useFeedback() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, participantProfile } = useAuth();

  const getContextInfo = useCallback((): FeedbackContextInfo => {
    const contextInfo = FeedbackContextService.getFeedbackContextInfo();
    
    // Enhance with auth context if available
    if (participantProfile?.id && !contextInfo.participantId) {
      contextInfo.participantId = participantProfile.id;
    }

    return contextInfo;
  }, [participantProfile]);

  const submitFeedback = useCallback(async (feedbackText: string): Promise<void> => {
    if (!user) {
      throw new Error('User must be authenticated to submit feedback');
    }

    if (!feedbackText.trim()) {
      throw new Error('Feedback text cannot be empty');
    }

    if (feedbackText.length > 500) {
      throw new Error('Feedback must be 500 characters or less');
    }

    setIsSubmitting(true);

    try {
      const contextInfo = getContextInfo();
      
      const submission: FeedbackSubmission = {
        feedbackText: feedbackText.trim(),
        submissionContext: contextInfo.context,
        sessionId: contextInfo.sessionId,
        participantId: contextInfo.participantId
      };

      // Non-blocking feedback submission to avoid disrupting annotation workflow
      // Use setTimeout to make it truly asynchronous and non-blocking
      setTimeout(async () => {
        try {
          console.log('Feedback submission (non-blocking):', {
            ...submission,
            userId: user.id,
            timestamp: new Date().toISOString()
          });

          // Get auth token
          const token = localStorage.getItem('auth-token');
          
          // Submit to server
          const response = await fetch('/api/feedback', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` })
            },
            body: JSON.stringify(submission)
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            console.error('Failed to submit feedback to server:', errorData);
          } else {
            const result = await response.json();
            console.log('Feedback submitted successfully:', result);
          }

        } catch (error) {
          // Log error but don't disrupt user experience
          console.error('Background feedback submission failed:', error);
        }
      }, 0);

      // Show immediate success message to user
      toast.success('Thank you for your feedback!');

    } catch (error) {
      console.error('Failed to prepare feedback submission:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit feedback';
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [user, getContextInfo]);

  return {
    submitFeedback,
    isSubmitting,
    getContextInfo
  };
}